import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useElements, useStripe, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { getCart, startEmbeddedPayment, releaseHolds, getQuote, finalizePayment } from '../api/reservationsApi';
import { useRef } from 'react';
import RulesSummary from '../components/RulesSummary';

// Elements and Stripe are provided by the wrapper (PaymentPageWrapper)

function amtLabel(value, type, currency = 'EUR') {
  if (value == null) return null;
  const t = String(type || '').toLowerCase();
  if (t === 'percentage' || t === 'percent') return `${Number(value)}% of booking`;
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(value)); }
  catch { return `€${Number(value).toFixed(2)}`; }
}

function InfoIcon({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button type="button" className="text-gray-500 hover:text-gray-800" aria-label={title || 'Info'} onClick={() => setOpen(!open)}>ⓘ</button>
      {open && (
        <div className="absolute z-10 mt-2 w-72 p-3 bg-white border rounded shadow text-xs">
          <div className="font-medium mb-1">{title || 'Info'}</div>
          <div className="space-y-1">{children}</div>
        </div>
      )}
    </div>
  );
}

function CartPreview({ items = [], currency = 'EUR' }) {
  return (
    <div className="border rounded-lg p-3 bg-white space-y-2">
      <div className="font-medium">Items</div>
      {items.map((it) => {
        const isBundle = it.offering_kind === 'bundle';
        const children = isBundle && Array.isArray(it.guests_json?.bundle_items) ? it.guests_json.bundle_items : [];
        const childSnaps = Array.isArray(it.policy_snapshot_json?.children) ? it.policy_snapshot_json.children : [];
        return (
          <div key={it.id}>
            <div className="text-sm flex justify-between">
              <div>
                <div className="font-medium">{it.title || it.offering_kind}</div>
                <div className="text-xs text-gray-500">
                  {it.offering_kind === 'ac_room_type'
                    ? `${it.guests_json?.ac_start_date} → ${it.guests_json?.ac_end_date}`
                    : (it.guests_json?.slot_start_local || it.slot_start || 'Open-dated')}
                </div>
                {(it.policy_snapshot_json?.fee_policy || it.policy_snapshot_json?.edit_rule) && (
                  <div className="mt-1 text-xs text-gray-600 flex items-center gap-1">
                    <InfoIcon title="Policies">
                      <RulesSummary
                        feePolicy={it.policy_snapshot_json?.fee_policy}
                        editRule={it.policy_snapshot_json?.edit_rule}
                        cancellationRules={it.policy_snapshot_json?.cancellation_rules || []}
                        currency={currency}
                        maxDateShiftDays={it.policy_snapshot_json?.max_date_shift_days}
                      />
                    </InfoIcon>
                  </div>
                )}
              </div>
              <div>{currency} {Number(it.base_price_preview || 0).toFixed(2)}</div>
            </div>
            {isBundle && children.length > 0 && (
              <div className="ml-3 mt-2 border-l pl-3 space-y-2">
                {children.map((c, idx) => {
                  const snap = childSnaps[idx] || {};
                  const when = c.ac_start_date && c.ac_end_date
                    ? `${c.ac_start_date} → ${c.ac_end_date}`
                    : (c.slot_start_local || c.slot_start || null);
                  return (
                    <div key={idx} className="text-xs flex justify-between">
                      <div>
                        <div className="font-medium text-gray-700">{String(c.mode || '').replace(/_/g,' ')}</div>
                        {when && <div className="text-[11px] text-gray-500">{when}</div>}
                        {(snap.fee_policy || snap.edit_rule) && (
                          <div className="mt-1 text-[11px] text-gray-600 flex items-center gap-1">
                            <InfoIcon title="Policies">
                              <RulesSummary
                                feePolicy={snap.fee_policy}
                                editRule={snap.edit_rule}
                                cancellationRules={snap.cancellation_rules || []}
                                currency={currency}
                                maxDateShiftDays={snap.max_date_shift_days}
                              />
                            </InfoIcon>
                          </div>
                        )}
                      </div>
                      <div className="text-gray-600">—</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {!items.length && <div className="text-xs text-gray-500">No items.</div>}
    </div>
  );
}

function CheckoutForm({ businessId, cartId, intentType, clientSecret, onDone }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!clientSecret) {
      setError('Payment is not ready yet. Please wait a moment…');
      return;
    }
    setLoading(true); setError('');
    try {
      // Create payment method from Card elements and confirm with clientSecret from backend
      const cardElement = elements.getElement(CardNumberElement);
      const return_url = `${window.location.origin}/reserve/${businessId}/checkout/success?cart_id=${encodeURIComponent(cartId)}`;

      if (intentType === 'payment') {
        const result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: { card: cardElement },
          // We can still set a return_url to handle 3DS if required
          return_url,
        });
        if (result.error) throw result.error;
        if (result.paymentIntent?.status === 'succeeded') onDone();
        else if (result.paymentIntent?.status === 'requires_action') {
          // Stripe handled redirect if needed; on return we'll land on success URL
          onDone();
        }
      } else {
        // SetupIntent flow
        const result = await stripe.confirmCardSetup(clientSecret, {
          payment_method: { card: cardElement },
          return_url,
        });
        if (result.error) throw result.error;
        if (result.setupIntent?.status === 'succeeded') onDone();
      }
    } catch (e) {
      setError(e?.message || 'Payment failed');
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">Card Number</label>
        <div className="border p-3 rounded">
          <CardNumberElement options={{ style: { base: { fontSize: '16px' } } }} />
        </div>
      </div>
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="text-sm font-medium text-gray-700 mb-1 block">Expiry</label>
          <div className="border p-3 rounded">
            <CardExpiryElement options={{ style: { base: { fontSize: '16px' } } }} />
          </div>
        </div>
        <div className="flex-1">
          <label className="text-sm font-medium text-gray-700 mb-1 block">CVC</label>
          <div className="border p-3 rounded">
            <CardCvcElement options={{ style: { base: { fontSize: '16px' } } }} />
          </div>
        </div>
      </div>
      {error && <div className="text-sm text-red-600">{error}</div>}
      <button disabled={!stripe || loading || !clientSecret} className="w-full py-2 rounded bg-black text-white disabled:opacity-40">
        {loading ? 'Processing…' : (!clientSecret ? 'Preparing…' : 'Pay')}
      </button>
      <div className="text-[11px] text-gray-500">Card saved for future charges where required.</div>
    </form>
  );
}

export default function PaymentPage({ businessId: propBusinessId, cartId: propCartId }) {
  const { businessId: routeBizId } = useParams();
  const [sp] = useSearchParams();
  const businessId = propBusinessId || routeBizId;
  const cartId = propCartId || sp.get('cart_id');
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [quote, setQuote] = useState(null);
  const [intent, setIntent] = useState(null); // { intentType, clientSecret, customerId, amount, currency }
  const stripe = useStripe();
  const shouldReleaseRef = useRef(true);
  // Timer: derive from query params (ttl seconds and t0 epoch ms)
  const ttlParam = Number(sp.get('ttl') || '0');
  const t0Param = Number(sp.get('t0') || '0');
  const [remaining, setRemaining] = useState(() => {
    if (!ttlParam || !t0Param) return 0;
    const elapsed = Math.floor((Date.now() - t0Param) / 1000);
    return Math.max(0, ttlParam - elapsed);
  });

  useEffect(() => {
    (async () => {
      if (!cartId) return;
      const [c, q] = await Promise.all([
        getCart(cartId),
        getQuote(businessId, cartId)
      ]);
      // merge policy snapshots into cart items to show info popovers
      const itemsById = Object.fromEntries((c?.items || []).map(it => [it.id, it]));
      (q?.items || []).forEach(it => { if (itemsById[it.id]) itemsById[it.id].policy_snapshot_json = it.policy_snapshot_json; });
      c.items = Object.values(itemsById);
      setCart(c);
      setQuote(q);
      const i = await startEmbeddedPayment(businessId, cartId);
      setIntent(i);
    })();
  }, [businessId, cartId]);

  // Countdown effect
  useEffect(() => {
    if (!ttlParam || !t0Param) return;
    const id = setInterval(() => {
      const elapsed = Math.floor((Date.now() - t0Param) / 1000);
      const left = Math.max(0, ttlParam - elapsed);
      setRemaining(left);
      if (left <= 0) {
        clearInterval(id);
        // Navigate back with expired message
        shouldReleaseRef.current = true; // allow release on unmount
        navigate(`/business/${businessId}/book?expired=1`, { replace: true });
      }
    }, 1000);
    return () => clearInterval(id);
  }, [ttlParam, t0Param, businessId, navigate]);

  // Release holds if user leaves the page without completing payment
  useEffect(() => {
    if (!businessId || !cartId) return;

    const handleBeforeUnload = () => {
      if (!shouldReleaseRef.current) return;
      try {
        const token = localStorage.getItem('token');
        fetch(`/reserve/${businessId}/holds?cart_id=${encodeURIComponent(cartId)}`,
          {
            method: 'DELETE',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            keepalive: true,
          }
        );
      } catch {}
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (shouldReleaseRef.current) {
        // Best-effort release on unmount
        releaseHolds(businessId, cartId).catch(() => {});
      }
    };
  }, [businessId, cartId]);

  if (!cartId) return <div className="p-4">Missing cart.</div>;

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-4">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="text-sm text-gray-600">← Back</button>
        <h1 className="text-xl font-semibold">Checkout</h1>
      </div>

      {remaining > 0 ? (
        <div className="rounded bg-orange-50 border border-orange-200 text-orange-800 p-2 text-sm">
          Your selection is reserved for <b>{Math.floor(remaining/60)}:{String(remaining%60).padStart(2,'0')}</b> minutes. Complete payment before it expires.
        </div>
      ) : (
        <div className="rounded bg-yellow-50 border border-yellow-200 text-yellow-800 p-2 text-sm">
          Session expired. Redirecting…
        </div>
      )}

      <CartPreview items={cart?.items || []} currency={cart?.currency || 'EUR'} />

      <div className="border rounded-lg p-3 bg-white text-sm">
        <div className="flex justify-between"><span>Subtotal</span><span>{(quote?.currency || cart?.currency || 'EUR')} {Number(quote?.subtotal || 0).toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Pay now</span><span>{(quote?.currency || cart?.currency || 'EUR')} {Number(intent?.amount ?? quote?.pay_now ?? 0).toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Hold (auth only)</span><span>{(quote?.currency || cart?.currency || 'EUR')} {Number(intent?.hold_amount ?? quote?.hold_amount ?? 0).toFixed(2)}</span></div>
        {Array.isArray(quote?.pay_later_schedule) && quote.pay_later_schedule.length > 0 && (
          <div className="mt-2">
            <div className="font-medium mb-1">Scheduled later charges</div>
            <ul className="list-disc ml-5 text-xs text-gray-600">
              {quote.pay_later_schedule.map((r, idx) => (
                <li key={idx}>{new Date(r.at).toLocaleString()} — {(quote?.currency || cart?.currency || 'EUR')} {Number(r.amount).toFixed(2)}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <CheckoutForm
        businessId={businessId}
        cartId={cartId}
        intentType={intent?.intentType}
        clientSecret={intent?.clientSecret}
          onDone={async () => {
            // Prevent releasing holds after successful payment (server will mark converted)
            try { await finalizePayment(businessId, cartId); } catch {}
            shouldReleaseRef.current = false;
            navigate(`/reserve/${businessId}/checkout/success?cart_id=${encodeURIComponent(cartId)}`);
          }}
      />
    </div>
  );
}
